/**
 * Chart maths, kept out of the components so it can be tested without a DOM.
 *
 * Nothing here knows about React or SVG elements — it turns numbers into other
 * numbers, and the components turn those into paths.
 */

/**
 * Axis ticks a person would have chosen: 1, 2, 5 and their powers of ten.
 *
 * The naive `max / count` produces axes labelled 0, 3.7, 7.4, 11.1 — which are
 * correct and unreadable. Rounding the STEP rather than the values is what
 * makes the labels land on numbers anyone can hold in their head.
 */
export const niceTicks = (max: number, count = 4): number[] => {
  if (!Number.isFinite(max) || max <= 0) return [0, 1];
  const rough = max / count;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const normalised = rough / magnitude;
  // Rounded UP to 1, 2, 5 or 10. Rounding down produces an axis whose top tick
  // sits below the data — the line then leaves the plot, which looks like a
  // rendering bug and is one.
  const step = (normalised <= 1 ? 1 : normalised <= 2 ? 2 : normalised <= 5 ? 5 : 10) * magnitude;
  const top = Math.ceil(max / step) * step;
  const ticks: number[] = [];
  for (let value = 0; value <= top + step * 1e-6; value += step) ticks.push(Number(value.toFixed(6)));
  // A single-tick axis is not an axis.
  if (ticks.length < 2) ticks.push(step);
  return ticks;
};

/** The top of the y-axis: the first nice tick at or above the data. */
export const axisMax = (max: number, count = 4): number => {
  const ticks = niceTicks(max, count);
  return ticks[ticks.length - 1] || 1;
};

export interface Plot {
  width: number;
  height: number;
  padTop: number;
  padRight: number;
  padBottom: number;
  padLeft: number;
}

/** Maps a value and an index onto the drawing area. */
export const scaler = (plot: Plot, count: number, max: number) => {
  const innerW = plot.width - plot.padLeft - plot.padRight;
  const innerH = plot.height - plot.padTop - plot.padBottom;
  const top = axisMax(max);
  return {
    innerW,
    innerH,
    top,
    // A single point sits in the middle rather than hard against the left edge,
    // where it reads as a rendering bug.
    x: (index: number) => plot.padLeft + (count <= 1 ? innerW / 2 : (index / (count - 1)) * innerW),
    y: (value: number) => plot.padTop + innerH - (top <= 0 ? 0 : (value / top) * innerH),
    band: (index: number) => plot.padLeft + (index / Math.max(1, count)) * innerW,
    bandWidth: innerW / Math.max(1, count),
  };
};

/** An SVG path through the points. */
export const linePath = (values: number[], scale: ReturnType<typeof scaler>): string =>
  values
    .map((value, index) => `${index === 0 ? 'M' : 'L'}${scale.x(index).toFixed(2)},${scale.y(value).toFixed(2)}`)
    .join(' ');

/** The same line closed down to the baseline, for the area fill beneath it. */
export const areaPath = (values: number[], scale: ReturnType<typeof scaler>, baseline: number): string => {
  if (!values.length) return '';
  const line = linePath(values, scale);
  const lastX = scale.x(values.length - 1).toFixed(2);
  const firstX = scale.x(0).toFixed(2);
  return `${line} L${lastX},${baseline.toFixed(2)} L${firstX},${baseline.toFixed(2)} Z`;
};

/**
 * Which point is nearest a pointer at `px`. Returns an index, so the crosshair
 * snaps to real data rather than floating between two points.
 */
export const nearestIndex = (px: number, count: number, scale: ReturnType<typeof scaler>): number => {
  if (count <= 1) return 0;
  let best = 0;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (let index = 0; index < count; index++) {
    const distance = Math.abs(scale.x(index) - px);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = index;
    }
  }
  return best;
};

/**
 * Donut arcs as stroke-dasharray offsets on a circle.
 *
 * A circle with a dash pattern is twelve lines of code where arc paths are
 * sixty, and it is exactly as accurate. The 2px gap between segments is the
 * surface spacer the design system asks for on adjacent fills.
 */
export const donutSegments = (values: number[], circumference: number, gap = 2) => {
  const total = values.reduce((sum, value) => sum + Math.max(0, value), 0);
  let offset = 0;
  return values.map((value) => {
    const share = total > 0 ? Math.max(0, value) / total : 0;
    const length = Math.max(0, share * circumference - gap);
    const segment = { length, offset: -offset, share };
    offset += share * circumference;
    return segment;
  });
};

/**
 * A cell's intensity, 0..1, for the traffic table and the click map.
 *
 * Square-rooted on purpose: click and traffic counts are heavily skewed, and a
 * linear ramp paints one hot cell and 167 that all look empty.
 */
export const intensity = (value: number, max: number): number => {
  if (!Number.isFinite(value) || value <= 0 || max <= 0) return 0;
  return Math.min(1, Math.sqrt(value / max));
};

/**
 * The fill for an intensity on the sequential ramp (`--viz-ramp-0..4`).
 *
 * Walked through ALL five stops rather than mixed straight from the first to
 * the last. A two-stop mix ignored the three middle steps, so the legend drew
 * five colours the cells were never painted in — and each theme's curve could
 * not be shaped at all: in dark it ran every cell through the same murky
 * mid-violets, and a week of traffic read as one purple slab. Mixed in oklab
 * so equal steps of intensity look like equal steps.
 */
export const rampFill = (weight: number): string => {
  if (!(weight > 0)) return 'var(--viz-ramp-0)';
  const position = Math.min(1, weight) * 4;
  const step = Math.min(3, Math.floor(position));
  const share = Math.round((position - step) * 100);
  return `color-mix(in oklab, var(--viz-ramp-${step + 1}) ${share}%, var(--viz-ramp-${step}))`;
};
